<template>
  <IndexView />
  <Toast />
  <ModalPin v-if="modalShowPin" @closeModal="closePinModal" :idGlobalDiscount="idGlobalDiscount"
    @submitpin="submitPin" />
  <ModalViewQrCode v-if="modalShowQrCode" @closeModal="closeQrCodeModal" :qrCode="qrCode" @submit="submitQrCode" />
  <ConfirmDialog group="headless">
    <template #container="{ message, acceptCallback, rejectCallback }">
      <div class="flex flex-col items-center p-8 bg-surface-0 dark:bg-surface-900 rounded">
        <div
          class="rounded-full bg-white outline outline-black inline-flex justify-center items-center h-24 w-24 -mt-20">
          <i class="pi pi-question text-5xl"></i>
        </div>
        <span class="font-bold text-2xl block mb-2 mt-6">{{ message.header }}</span>
        <p class="mb-0">{{ message.message }}</p>
        <div class="flex items-center gap-2 mt-6">
          <Button :label="message.rejectLabel" outlined @click="rejectCallback" class="w-32"></Button>
          <Button :label="message.acceptLabel" @click="acceptCallback" class="w-32"></Button>
        </div>
      </div>
    </template>
  </ConfirmDialog>
  <ViewContainer>
    <Dialog v-model:visible="showErrorDialog" modal :header="'Terjadi Kesalahan'" :closable="true"
      class="w-[90vw] md:w-[50vw]">
      <div class="flex items-start space-x-3">
        <ExclamationCircleIcon class="h-6 w-6 text-red-500" />
        <p class="text-gray-700 dark:text-gray-200">{{ errorMessage }}</p>
      </div>
      <template #footer>
        <Button label="Tutup" @click="showErrorDialog = false" severity="secondary" />
      </template>
    </Dialog>
    <Dialog v-model:visible="showPrintConfirmDialog" modal :header="'Konfirmasi Print'" :closable="true"
      class="w-[90vw] md:w-[50vw]">
      <div class="flex items-start space-x-3">
        <PrinterIcon class="h-6 w-6 text-blue-500" />
        <p class="text-gray-700 dark:text-gray-200">
          Apakah Anda yakin ingin mencetak
          {{ printDivId === "receipt-print" ? "struk" : "bill" }}?
        </p>
      </div>
      <template #footer>
        <div class="flex gap-2">
          <Button label="Batal" @click="showPrintConfirmDialog = false" severity="secondary" />
          <Button label="Ya, Cetak" @click="confirmPrint" severity="primary" />
        </div>
      </template>
    </Dialog>

    <div v-if="isLoading" class="p-6 flex justify-center items-center">
      <ProgressSpinner />
    </div>
    <div v-else class="p-6">
      <PageTitle :title="'Payment: Order #' +
        this.transaction?.code
        " />

      <div class="flex flex-col md:flex-row justify-center gap-2 max-w-5xl mx-auto">
        <div class="w-full mx-auto bg-white dark:bg-dark-900 rounded-lg shadow-md p-6 gap-5">
          <!-- Payment Status -->
          <div v-if="this.transaction?.status === 'paid'"
            class="mb-6 flex items-center justify-between bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-900 rounded-lg p-4">
            <div class="flex items-center space-x-3">
              <div class="flex-shrink-0">
                <CheckCircleIcon class="h-6 w-6 text-green-400" />
              </div>
              <div>
                <h3 class="text-base font-medium text-green-800 dark:text-green-200">
                  Pembayaran Selesai
                </h3>
                <p class="text-sm text-green-700 dark:text-green-300">
                  Transaksi ini telah dibayar
                </p>
              </div>
            </div>
          </div>

          <!-- Detail Transaksi -->
          <h2 class="text-xl font-semibold mb-4 text-gray-600 dark:text-gray-300 uppercase">
            Detail Transaksi
          </h2>
          <div v-for="detail in viewDetailTransactionForDetail" :key="detail.id"
            class="flex justify-between mt-1 border-gray-200 dark:border-gray-700 border-b">
            <h3 class="text-base font-light text-gray-600 dark:text-gray-300">
              {{ detail.field }}
            </h3>
            <p class="text-base font-semibold text-gray-800 dark:text-gray-100">
              {{
                detail.type === "rupiah"
                  ? formatRupiah(detail.value)
                  : detail.value
              }}
            </p>
          </div>

          <!-- Daftar Item -->
          <h2 class="text-xl font-semibold mb-4 text-gray-600 dark:text-gray-300 uppercase mt-4">
            Daftar Item
          </h2>
          <ListItemPayment :items="order_items" />
        </div>

        <div class="w-full mx-auto bg-white dark:bg-dark-900 rounded-lg shadow-md p-6 gap-5">
          <!-- Total Pembayaran -->
          <div class="flex justify-between border-gray-200 dark:border-gray-700">
            <h3 class="text-base font-semibold text-gray-600 dark:text-gray-300">
              Sub Total
            </h3>
            <p class="text-base font-semibold text-gray-800 dark:text-gray-100">
              {{ formatRupiah(subTotalFrontEnd) }}
            </p>
          </div>
          <div class="flex justify-between mt-2 border-gray-200 dark:border-gray-700">
            <h3 class="text-base font-semibold text-gray-600 dark:text-gray-300">
              Discount
            </h3>
            <p class="text-base font-semibold text-red-500 dark:text-red-500">
              {{ formatRupiah(discountTotalFrontEnd) }}
            </p>
          </div>
          <div class="flex justify-between mt-2 border-gray-200 dark:border-gray-700">
            <h3 class="text-base font-semibold text-gray-600 dark:text-gray-300">
              Global Discount
            </h3>
            <p v-if="['paid', 'canceled'].includes(this.transaction?.status)"
              class="text-base font-semibold text-red-500 dark:text-red-500">
              {{ formatRupiah(globalDiscountTotalFrontEnd) }}
            </p>
            <InputSelect v-else id="pilih-diskon-global" v-model="selectedGlobalDiscountId"
              :options="optionsGlobalDiscount" placeholder="Pilih discount global" />
          </div>
          <div class="flex justify-end mt-2 border-gray-200 dark:border-gray-700">
            <p v-if="!['paid', 'canceled'].includes(this.transaction?.status)"
              class="text-base font-semibold text-red-500 dark:text-red-500">
              {{ formatRupiah(globalDiscountTotalFrontEnd) }}
            </p>
          </div>
          <div class="flex justify-between mt-2 border-gray-200 dark:border-gray-700">
            <h3 class="text-base font-semibold text-gray-600 dark:text-gray-300">
              Service Charge {{ Number(this.allSettings[0]?.service_charge) }}%
            </h3>
            <p class="text-base font-semibold text-gray-800 dark:text-gray-100">
              {{ formatRupiah(serviceChargeTotalFrontEnd) }}
            </p>
          </div>
          <div class="flex justify-between my-3 border-gray-200 dark:border-gray-700">
            <h3 class="text-base font-semibold text-gray-600 dark:text-gray-300">
              Tax {{ Number(this.allSettings[0]?.tax) }}%
            </h3>
            <p class="text-base font-semibold text-gray-800 dark:text-gray-100">
              {{ formatRupiah(serviceTaxTotalFrontEnd) }}
            </p>
          </div>
          <div v-if="
            this.transaction?.payment_amount > 0 &&
            ['paid', 'canceled'].includes(this.transaction?.status)
          " class="flex justify-between my-3 border-gray-200 dark:border-gray-700">
            <h3 class="text-base font-semibold text-gray-600 dark:text-gray-300">
              Delivery Fee
            </h3>
            <p class="text-base font-semibold text-gray-800 dark:text-gray-100">
              {{ formatRupiah(this.transaction?.delivery_fee) }}
            </p>
          </div>
          <InputRupiah v-else v-model="deliveryFee" id="delivery-fee" label="Delivery Fee" input_class="w-full text-2xl text-right" />
          <div class="flex justify-between mt-2 border-t pt-4 border-gray-200 dark:border-gray-700">
            <h3 class="text-xl font-bold text-gray-600 dark:text-gray-300 uppercase">
              Grand Total
            </h3>
            <p class="text-xl font-bold text-gray-800 dark:text-gray-100">
              {{ formatRupiah(grandTotalFrontEnd) }}
            </p>
          </div>
          <div v-if="this.transaction?.payment_amount > 0 && ['paid', 'canceled'].includes(this.transaction?.status)"
            class="flex justify-between mt-2 border-t pt-4 border-gray-200 dark:border-gray-700">
            <h3 class="text-base font-semibold text-gray-600 dark:text-gray-300">
              Payment
            </h3>
            <p class="text-base font-semibold text-gray-800 dark:text-gray-100">
              {{ formatRupiah(this.transaction?.payment_amount) }}
            </p>
          </div>
          <div v-if="
            this.transaction?.payment_amount > 0 &&
            this.transaction?.status == 'paid'
          " class="flex justify-between mt-2 border-t pt-4 border-gray-200 dark:border-gray-700">
            <h3 class="text-base font-semibold text-gray-600 dark:text-gray-300">
              Metode Pembayaran
            </h3>
            <p class="text-base font-semibold text-gray-800 dark:text-gray-100">
              {{ paymentMethode?.[this.transaction?.payment_method?.name?.toUpperCase()] || this.transaction?.payment_method?.name || '-' }}
            </p>
          </div>
          <div v-if="
            this.transaction?.payment_amount > 0 &&
            ['paid', 'canceled'].includes(this.transaction?.status)
          " class="flex justify-between mt-2 border-t pt-4 border-gray-200 dark:border-gray-700">
            <h3 class="text-base font-semibold text-gray-600 dark:text-gray-300">
              Catatan
            </h3>
            <p class="text-base font-semibold text-gray-800 dark:text-gray-100">
              {{ this.transaction?.note }}
            </p>
          </div>
          <div v-if="this.transaction?.payment_change > 0 && ['paid'].includes(this.transaction?.status)"
            class="flex justify-between mt-2 pt-4 border-gray-200 dark:border-gray-700">
            <h3 class="text-base font-semibold text-gray-600 dark:text-gray-300">
              Kembalian
            </h3>
            <p class="text-base font-semibold text-gray-800 dark:text-gray-100">
              {{
                formatRupiah(
                  this.transaction?.payment_change
                )
              }}
            </p>
          </div>
          <!-- Tombol Lanjut ke Pembayaran -->
          <div v-if="!showPaymentForm && !['paid', 'canceled'].includes(transaction?.status)" class="flex flex-col sm:flex-row gap-2 mt-4">
            <div class="flex gap-2 w-full">
              <button type="button"
                class="flex-1 flex items-center justify-center gap-2 bg-info text-white py-2 rounded-md hover:bg-hover-info disabled:opacity-50 disabled:cursor-not-allowed"
                @click="printDocumentBluetooth('bill-print')">
                <PrinterIcon class="w-5 h-5" />
                <span>Print Bill</span>
              </button>
            </div>
            <button label="Lanjut ke Pembayaran" severity="success" @click="showPaymentForm = true"
              class="w-full bg-success hover:bg-hover-success dark:bg-green-600 px-4 py-2 rounded-lg text-white font-semibold uppercase tracking-wide">
              Lanjut ke Pembayaran
            </button>
          </div>
          <div v-else-if="['paid', 'canceled'].includes(transaction?.status)" class="flex flex-col sm:flex-row gap-2 mt-4">
            <div class="flex gap-2 w-full">
              <button type="button"
                class="flex-1 flex items-center justify-center gap-2 bg-green-400 dark:bg-green-600 text-white uppercase font-semibold py-2 rounded-md hover:bg-green-500 dark:hover:bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                @click="printDocumentBluetooth('receipt-print')" :disabled="transaction?.status !== 'paid'">
                <PrinterIcon class="w-5 h-5" />
                <span>Print Close Bill</span>
              </button>
            </div>
          </div>

          <!-- Form Pembayaran -->
          <div v-show="showPaymentForm" class="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4">
            <h2 class="text-xl font-semibold mb-4 text-gray-600 dark:text-gray-300 uppercase">
              Metode Pembayaran
            </h2>
            <form @submit.prevent="submitPayment" class="space-y-4">
              <!-- Pilihan Metode Pembayaran -->
              <div class="space-y-2">
                <div class="flex flex-wrap gap-2">
                  <Button v-for="method in allPaymentMethods" :key="method.id" :severity="selectedPaymentMethodId === method.id
                    ? 'primary'
                    : 'secondary'
                    " :outlined="selectedPaymentMethodId !== method.id" @click="selectPaymentMethod(method)"
                    type="button" class="flex-1 min-w-[120px] justify-center uppercase font-bold">
                    {{ paymentMethode?.[method?.name?.toUpperCase()] || method.name }}
                  </Button>
                </div>
              </div>
              <template v-if="selectedPaymentMethodId">
                <!-- Input Pembayaran Tunai -->
                <template v-if="isCashPayment">
                  <InputRupiah v-model="payment" id="payment-amount" label="Jumlah Pembayaran"
                    input_class="w-full text-2xl text-right" required disabled />
                  <CashierKeyboard v-model="payment" />
                  <div class="flex justify-between mt-2 border-gray-200 dark:border-gray-700">
                    <h3 class="text-base font-semibold text-gray-600 dark:text-gray-300">
                      Kembalian
                    </h3>
                    <p class="text-base font-semibold text-gray-800 dark:text-gray-100">
                      {{
                        formatRupiah(paymentChange)
                      }}
                    </p>
                  </div>
                </template>

                <!-- Nama Pelanggan -->
                <div v-if="isHutangPayment.isName" class="mb-4">
                  <InputTextCustom v-model="customerName" id="customer-name" label="Nama Pelanggan"
                    placeholder="Masukkan nama pelanggan" :valid="validationCloseOrders.customer_name.valid"
                    :error-message="validationCloseOrders.customer_name.message" />
                </div>
                <!-- Nomor Telepon -->
                <div v-if="isHutangPayment.isHp" class="mb-4">
                  <InputTextCustom v-model="customerPhone" id="customer-hp" label="Nomor Telepon"
                    placeholder="Masukkan nomor telepon" :valid="validationCloseOrders.customer_phone.valid"
                    :error-message="validationCloseOrders.customer_phone.message" />
                </div>
                <!-- Marketing -->
                <div v-if="isHutangPayment.isMarketing" class="mb-4">
                  <InputSelect v-model="marketingId" id="marketing-id" label="Staff" :options="optionMarketing"
                    placeholder="Pilih Staff" :valid="validationCloseOrders.marketing_id.valid"
                    :error-message="validationCloseOrders.marketing_id.message" />
                </div>
                <!-- Catatan -->
                <div class="mb-4">
                  <label for="notes" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Catatan
                    (Opsional)</label>
                  <textarea v-model="notes" id="notes" rows="2"
                    class="mt-2 block w-full border rounded-md shadow-sm p-2 bg-white dark:bg-dark-800 dark:text-gray-300"></textarea>
                </div>
                <div class="flex flex-col justify-between gap-4">
                  <button type="button"
                    class="w-full flex items-center justify-center gap-2 bg-info hover:bg-hover-info text-white py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                    @click="printDocumentBluetooth('bill-print')">
                    <PrinterIcon class="w-5 h-5" />
                    <span>Print Bill</span>
                  </button>
                  <button v-if="this.transaction?.status === 'paid' " type="button"
                    class="w-full flex items-center justify-center gap-2 bg-info hover:bg-hover-info text-white py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                    @click="printDocumentBluetooth('receipt-print')" :disabled="this.transaction?.status !== 'paid'">
                    <PrinterIcon class="w-5 h-5" />
                    <span>Print Close Bill</span>
                  </button>
                  <button v-if="this.isPreviewQrcode" type="submit"
                    class="w-full bg-success hover:bg-hover-success dark:bg-green-600 text-white py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed font-semibold uppercase tracking-wide"
                    :disabled="this.transaction?.status === 'paid'">
                    Konfirmasi Pembayaran
                  </button>
                  <button v-if="!this.isPreviewQrcode" type="button"
                    class="w-full bg-warning hover:bg-hover-warning dark:bg-warning-600 text-primary py-2 rounded-md hover:bg-warning-600 disabled:opacity-50 disabled:cursor-not-allowed font-semibold uppercase tracking-wide"
                    @click="showModalQrCode"
                    :disabled="this.transaction?.status === 'paid'">
                    Preview QR Code
                  </button>
                </div>
              </template>
            </form>
          </div>
        </div>
      </div>

      <!-- Bluetooth Printer Connection -->
      <div class="flex flex-wrap items-center justify-center gap-3 mt-4 mb-6 p-4 bg-gray-50 dark:bg-dark-800 rounded-lg">
        <div class="flex items-center gap-2">
          <i class="pi pi-print text-lg"></i>
          <span class="font-medium">Printer Bluetooth:</span>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <Button 
            :icon="isConnected ? 'pi pi-check-circle' : 'pi pi-bluetooth'" 
            :label="isConnected ? `Terkoneksi: ${deviceName}` : 'Sambungkan Printer'"
            :severity="isConnected ? 'success' : 'primary'"
            :loading="isConnecting"
            :disabled="isConnecting"
            @click="connectBluetooth"
            class="min-w-[200px]"
          />
          <Button 
            v-if="isConnected" 
            icon="pi pi-times" 
            label="Putuskan" 
            severity="danger" 
            outlined
            @click="disconnectBluetooth"
            :disabled="isConnecting"
          />
        </div>
        <div v-if="connectionError" class="w-full text-red-500 text-sm mt-2">
          {{ connectionError }}
        </div>
      </div>
    </div>

    <!-- Print sections with print-specific styles -->
    <div class="print-section" id="receipt-print" :class="{ hidden: true }">
      <ReceiptPrintNoJSP :items="order_items" :viewdetailtotal="viewDetailTotal"
        :viewdetailtransaksi="viewDetailTransaksi" :viewpayment="viewPayment" :createdAt="transaction?.createdAt" />
    </div>
    <div class="print-section" id="bill-print" :class="{ hidden: true }">
      <BillPrint :items="order_items" :viewdetailtotal="viewDetailTotal" :viewdetailtransaksi="viewDetailTransaksi"
        :currentGlobalDiscount="currentGlobalDiscount" />
    </div>

    <!-- Add Print Preview Dialog -->
    <Dialog v-model:visible="showPrintPreview" modal :header="'Print Preview'" :closable="true"
      class="w-[90vw] md:w-[70vw] h-[90vh]">
      <div class="flex flex-col h-full">
        <div class="flex-1 overflow-auto bg-white dark:bg-dark-800 p-4 rounded-lg">
          <div v-if="printDivId === 'receipt-print'" class="print-preview">
            <ReceiptPrintNoJSP :items="order_items" :viewdetailtotal="viewDetailTotal"
              :viewdetailtransaksi="viewDetailTransaksi" :viewpayment="viewPayment" :ishide="false" />
          </div>
          <div v-else class="print-preview">
            <BillPrint :items="order_items" :viewdetailtotal="viewDetailTotal"
              :viewdetailtransaksi="viewDetailTransaksi" :ishide="false" />
          </div>
        </div>
        <div class="flex justify-end gap-2 mt-4">
          <Button label="Batal" @click="showPrintPreview = false" severity="secondary" />
          <Button label="Print" @click="proceedWithPrint" severity="primary" />
        </div>
      </div>
    </Dialog>
  </ViewContainer>
</template>

<script>
import axios from "axios";
import PageTitle from "@/components/PageTitle.vue";
import IndexView from "@/views/IndexView.vue";
import ListItemPayment from "./ListItemPayment.vue";
import { formatRupiah } from "@/helpers/formatRupiah";
import print from "@/helpers/print.js";
import {
  PrinterIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from "@heroicons/vue/24/solid";
import { mapActions, mapGetters, mapState } from "vuex";
import InputRupiah from "@/components/form/InputRupiah.vue";
import InputSelect from "@/components/form/InputSelect.vue";
import ViewContainer from "@/components/ViewContainer.vue";
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import ProgressSpinner from "primevue/progressspinner";
import CashierKeyboard from "@/components/CashierKeyboard.vue";
import ReceiptPrintNoJSP from "@/components/print/receipt/ReceiptPrintNoJSP.vue";
import BillPrint from "@/components/print/receipt/BillPrint.vue";
import ModalPin from "./ModalPin.vue";
import { ConfirmDialog } from "primevue";
import InputTextCustom from "@/components/form/InputTextCustom.vue";
import { selectedQris } from "@/helpers/qris";
import ModalViewQrCode from "@/components/modals/ModalViewQrCode.vue";
import Toast from "primevue/toast";
import { paymentMethode } from "@/helpers/constanta";

export default {
  name: "PaymentView",
  components: {
    PageTitle,
    IndexView,
    ListItemPayment,
    PrinterIcon,
    InputRupiah,
    InputSelect,
    ViewContainer,
    CheckCircleIcon,
    Dialog,
    Button,
    ExclamationCircleIcon,
    ProgressSpinner,
    CashierKeyboard,
    ReceiptPrintNoJSP,
    BillPrint,
    ModalPin,
    ConfirmDialog,
    InputTextCustom,
    ModalViewQrCode,
    Toast,
  },
  data() {
    return {
      selectedPaymentMethodId: null,
      selectedGlobalDiscountId: null,
      paymentMethode,
      payment: 0,
      notes: "",
      showErrorDialog: false,
      errorMessage: "",
      isLoading: true,
      order_items: [],
      showPaymentForm: false,
      transaction: [],
      showPrintConfirmDialog: false,
      printDivId: null,
      showPrintPreview: false,
      modalShowPin: false,
      idGlobalDiscount: null,
      customerName: '',
      customerPhone: '',
      marketingId: null,
      fieldCloseOrders: ['customer_name', 'customer_phone', 'marketing_id'],
      isPreviewQrcode: false,
      modalShowQrCode: false,
      qrCode: null,
      validationCloseOrders: {
        customer_name: {
          valid: true,
          message: ''
        },
        customer_phone: {
          valid: true,
          message: ''
        },
        marketing_id: {
          valid: true,
          message: ''
        }
      },
      deliveryFee: 0,
    };
  },
  computed: {
    ...mapGetters("tables", ["allTables"]),
    ...mapGetters("payment_methods", ["allPaymentMethods"]),
    ...mapGetters("marketings", ["allMarketings"]),
    ...mapGetters("global_discounts", ["allGlobalDiscounts"]),
    ...mapGetters("settings", ["allSettings"]),
    ...mapGetters('detail_user', ['detailUser']),
    ...mapState('bluetooth', ['isConnecting', 'error']),
    ...mapGetters('bluetooth', ['isConnected', 'deviceName']),
    connectionError() {
      return this.error?.message || null;
    },
    currentTable() {
      const id = this.transaction?.table;
      return this.allTables?.find((table) => table.id == id);
    },
    optionMarketing() {
      return this.allMarketings.map((marketing) => ({
        value: marketing.id,
        label: marketing.name,
      }));
    },
    currentMarketing() {
      const id = this.transaction?.marketing_id;
      console.log(id);
      return this.allMarketings?.find((marketing) => marketing.id == id);
    },
    viewDetailTransactionForDetail() {
      if (!this.transaction) return [];
      return [
        {
          field: "No. Invoice",
          value: this.transaction?.code || "-",
          type: "text",
        },
        {
          field: "Tanggal",
          value: this.transaction?.createdAt ? this.formatDate(this.transaction?.createdAt) : "-",
          type: "text",
        },
        {
          field: "Nama Tamu",
          value: this.transaction?.customer_name || "-",
          type: "text",
        },
        {
          field: "Kasir",
          value: this.transaction?.cashier_name || "-",
          type: "text",
        },
        {
          field: "Status",
          value: this.transaction?.status || "-",
          type: "text",
        },
      ];
    },
    viewDetailTransaksi() {
      return [
        {
          field: "No. Invoice",
          value: this.transaction?.code || "-",
          type: "text",
        },
        { field: "Tanggal", value: this.transaction?.createdAt ? this.formatDatePrint(this.transaction?.createdAt) : "-", type: "text" },
        {
          field: "Nama Tamu",
          value: this.transaction?.customer_name || "-",
          type: "text",
        },
        {
          field: "Kasir",
          value: this.transaction?.cashier_name || "-",
          type: "text",
        },
      ];
    },
    total() {
      return this.order_items?.reduce(
        (sum, item) => sum + item.price * item.quantity,
        0
      );
    },
    isHutangPayment() {
      const selectedMethod = this.allPaymentMethods?.find(
        (method) => method.id === this.selectedPaymentMethodId
      );
      let isHutang = selectedMethod?.name?.toLowerCase() === "hutang";

      if (!isHutang || ["paid", "canceled"].includes(this.transaction?.status)) {
        return {
          isName: false,
          isHp: false,
          isMarketing: false
        }
      }
      return {
        isName: this.transaction?.customer_name ? false : true,
        isHp: this.transaction?.customer_phone ? false : true,
        isMarketing: this.currentMarketing ? false : true,
        isHutang: isHutang
      };
    },
    isCloseOrder() {
      const closeStatus = ["canceled", "paid"];
      return closeStatus.includes(this.transaction?.status);
    },
    isCashPayment() {
      const selectedMethod = this.allPaymentMethods?.find(
        (method) => method.id === this.selectedPaymentMethodId
      );
      return selectedMethod?.name?.toLowerCase() === "cash";
    },
    isValidPayment() {
      if (!this.selectedPaymentMethodId) return false;
      if (!this.isCashPayment) return true;
      return this.payment >= this.transaction?.grand_total;
    },
    subTotalFrontEnd() {
      console.log(this.order_items);
      return this.order_items?.reduce(
        (sum, item) =>
          sum +
          (item.status == "canceled" ? -1 : 1) *
          (item.price * item.quantity),
        0
      );
    },
    discountTotalFrontEnd() {
      return this.order_items?.reduce(
        (sum, item) =>
          sum +
          (item.status == "canceled" ? -1 : 1) *
          (item.discount_price * item.quantity),
        0
      );
    },
    globalDiscountTotalFrontEnd() {
      if (this.currentGlobalDiscount) {
        this.order_items.map((item) => {
          item.globalDiscountAmount = (item.discount_applied ? ((item.price - item.discount_price) * (this.currentGlobalDiscount.amount / 100)) : 0);
          item.totalAfterGlobalDiscount = item.total - item.globalDiscountAmount;
          return item;
        });
        return this.order_items?.reduce(
          (sum, item) => sum + (item.discount_applied ? (item.status == "canceled" ? -1 : 1) * item.globalDiscountAmount * item.quantity : 0),
          0
        );
      } else {
        return 0;
      }
    },
    serviceTaxTotalFrontEnd() {
      const settings = this.allSettings.length > 0 ? this.allSettings[0] : {};
      let result = this.order_items?.reduce(
        (sum, item) =>
          sum + (item.tax_applied ? (item.status == "canceled" ? -1 : 1) * (settings.tax / 100) * (item.price - item.discount_price) * item.quantity : 0),
        0
      )
      return result;
    },
    serviceChargeTotalFrontEnd() {
      const settings = this.allSettings.length > 0 ? this.allSettings[0] : {};
      this.globalDiscountTotalFrontEnd;
      console.log('serviceChargeTotalFrontEnd', this.order_items);
      return this.order_items?.reduce(
        (sum, item) =>
          sum + (item.service_charge_applied ? (item.status == "canceled" ? -1 : 1) * ((settings.service_charge / 100) * (this.globalDiscountTotalFrontEnd ? (item.totalAfterGlobalDiscount * item.quantity) : item.total * item.quantity)) : 0),
        0
      );
    },
    grandTotalFrontEnd() {
      let globalDiscountAmount = ["paid", "canceled"].includes(this.transaction?.status) ? this.transaction?.global_discount_amount : this.globalDiscountTotalFrontEnd;
      return this.subTotalFrontEnd + this.serviceTaxTotalFrontEnd + this.serviceChargeTotalFrontEnd - this.discountTotalFrontEnd - globalDiscountAmount + this.deliveryFee;
    },
    optionsGlobalDiscount() {
      let options = [];
      options.push({ value: null, label: "Pilih Discount Global" });
      options = options.concat(this.allGlobalDiscounts.map((gb) => ({
        value: gb.id,
        label: `${gb.name} (${Number(gb.amount)}%)`,
      })))
      return options;
    },
    currentGlobalDiscount() {
      let idGlobalDiscount = ["paid", "canceled"].includes(this.transaction?.status) ? this.transaction?.global_discount_id : this.selectedGlobalDiscountId;
      return this.allGlobalDiscounts.find(gb => gb.id == idGlobalDiscount);
    },
    paymentChange() {
      return this.payment - Math.ceil(this.grandTotalFrontEnd);
    },
    viewDetailTotal() {
      let result = [
        {
          field: "Sub Total",
          value: `Rp${Number(this.subTotalFrontEnd).toLocaleString()}` || "-",
          type: "text",
        },
      ];

      if (this.discountTotalFrontEnd > 0) {
        result.push({
          field: "Diskon",
          value: `-Rp${Number(this.discountTotalFrontEnd).toLocaleString()}` || "-",
          type: "text",
        });
      }
      if (this.globalDiscountTotalFrontEnd > 0) {
        result.push({
          field: "Global Discount",
          value: `-Rp${Number(this.globalDiscountTotalFrontEnd).toLocaleString()}` || "-",
          type: "text",
        });
      }

      if (this.serviceChargeTotalFrontEnd > 0) {
        result.push({
          field: "Service Charge",
          value: `Rp${Number(this.serviceChargeTotalFrontEnd).toLocaleString()}` || "-",
          type: "text",
        });
      }

      if (this.serviceTaxTotalFrontEnd > 0) {
        result.push({
          field: "Service Tax",
          value: `Rp${Number(this.serviceTaxTotalFrontEnd).toLocaleString()}` || "-",
          type: "text",
        });
      }

      result.push({
        field: "Total",
        value: `Rp${Number(this.grandTotalFrontEnd).toLocaleString()}` || "-",
        type: "text",
      });
      return result;
    },
    viewPayment() {
      let result = [];
      result.push({ field: "Metode Pembayaran", value: this.transaction?.payment_method?.name || "-", type: "text" });
      result.push({
        field: "Total Bill",
        value: this.transaction?.grand_total || "-",
        type: "rupiah",
      },
        {
          field: "Bayar",
          value: this.payment,
          type: "rupiah",
        },
        {
          field: "Kembalian",
          value: this.transaction?.payment_change && this.transaction?.payment_change > 0 ? this.transaction?.payment_change : this.paymentChange,
          type: "rupiah",
        },
      );
      return result;
    },
  },
  watch: {
    selectedGlobalDiscountId: {
      immediate: true,
      handler(newId) {
        const globalDiscount = this.allGlobalDiscounts.find(
          (gb) => gb.id == newId
        );
        if (globalDiscount) {
          if (globalDiscount?.need_pin) {
            this.showModalPIN();
          } else {
            this.closePinModal(false);
          }
        } else {
          this.closePinModal();
        }
      },
    },
  },
  async created() {
    try {
      // Fetch required data in parallel
      await Promise.all([
        this.$store.dispatch("settings/fetchSettings", { limit: 1000 }),
        this.$store.dispatch('detail_user/fetchUser'),
        this.$store.dispatch("tables/fetchTables", { limit: 1000 }),
        this.$store.dispatch("payment_methods/fetchPaymentMethods", {
          limit: 1000,
        }),
        this.$store.dispatch("marketings/fetchMarketings", { limit: 1000 }),
        this.$store.dispatch("global_discounts/fetchGlobalDiscounts", { limit: 1000 }),
      ]);
    } catch (error) {
      this.errorMessage =
        error.message ||
        "Terjadi kesalahan saat memuat data. Silakan coba lagi.";
      this.showErrorDialog = true;
      this.isLoading = false;
    }
    finally {
      this.checkAndRefreshOrder();
    }
  },
  methods: {
    ...mapActions("sales_orders", ["closeSalesOrder"]),
    ...mapActions("tables", ["updateTable"]),
    ...mapActions('bluetooth', ['connectBluetooth', 'disconnectBluetooth', 'sendToPrinter', 'startPrinterJob', 'endPrinterJob']),
    formatRupiah,
    formatPaymentMethodeName(paymentMethodId) {
      const paymentMethod = this.allPaymentMethods.find(paymentMethod => paymentMethod.id === paymentMethodId);
      return paymentMethode?.[paymentMethod?.name?.toUpperCase()] || paymentMethod?.name || '-';
    },
    formatDate(date) {
      if (!date) return "-";
      const d = new Date(date);
      const pad = (num) => num.toString().padStart(2, "0");

      const days = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
      const months = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];

      const dayName = days[d.getDay()];
      const day = pad(d.getDate());
      const month = months[d.getMonth()];
      const year = d.getFullYear();
      const hours = pad(d.getHours());
      const minutes = pad(d.getMinutes());
      const seconds = pad(d.getSeconds());

      return `${dayName}, ${day} ${month} ${year} ${hours}:${minutes}:${seconds}`;
    },
    formatDatePrint(date) {
      if (!date) return "-";
      const d = new Date(date);
      const pad = (num) => num.toString().padStart(2, "0");

      const months = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];

      const day = pad(d.getDate());
      const month = months[d.getMonth()];
      const year = d.getFullYear();

      return `${day} ${month} ${year}`;
    },
    async checkAndRefreshOrder() {
      this.order_id = this.$route.query.orderId;
      if (this.order_id != null) {
        const response = await this.$store.dispatch(
          "sales_orders/fetchSalesOrderById",
          this.order_id
        );
        const salesOrder = response.data;
        const cashierId = salesOrder?.status == "pending" ? this.detailUser.id : (salesOrder.create_by ? salesOrder.update_by : salesOrder.create_by);

        const responseUser = cashierId ? await this.$store.dispatch("users/fetchUserById", cashierId) : null;

        this.transaction = {
          id: salesOrder.id,
          code: salesOrder.code,
          table: salesOrder.table_id,
          marketing_id: salesOrder.marketing_id,
          cash_register_id:
            salesOrder.cash_register_id == null
              ? this.relevantCashRegister?.id
              : salesOrder.cash_register_id,
          transaction_type_id: salesOrder.transaction_type_id,
          customer_name: salesOrder.customer_name,
          customer_phone: salesOrder.customer_phone,
          order_type: salesOrder.order_type,
          note: salesOrder.note,
          cashier_name: responseUser?.data
            ? responseUser.data?.name
            : "Unknown",
          total: salesOrder.sub_total,
          discount: salesOrder.discount,
          service_charge: salesOrder.service_charge,
          service_tax: salesOrder.tax,
          grand_total: salesOrder.grand_total,
          status: salesOrder.status,
          payment_amount: salesOrder.payment_amount,
          payment_change: salesOrder.payment_change,
          payment_method: this.allPaymentMethods.find(
            (p) => p.id == salesOrder.payment_method_id
          ),
          createdAt: salesOrder.createdAt,
          global_discount_amount: salesOrder.global_discount_amount,
          global_discount_id: salesOrder.global_discount_id,
          outlet_id: salesOrder.outlet_id,
          delivery_fee: salesOrder.delivery_fee,
        };
        this.payment = salesOrder.payment_amount;
        this.selectedGlobalDiscountId = salesOrder.global_discount_id;
        this.order_items = salesOrder.menus.map((menu) => {
          return {
            id: menu.id,
            item_id: menu.item_id,
            name: menu?.items?.name,
            quantity: menu.qty,
            price: menu.price,
            subtotal: menu.qty * menu.price,
            payment_status: 1,
            note: menu.note,
            isEdit: false,
            isPrint: true,
            discount_price: menu.discount_price || 0,
            total: menu.price - menu.discount_price,
            createdAt: menu.createdAt,
            status: menu.status,
            service_charge_applied: menu.service_charge_applied,
            tax_applied: menu.tax_applied,
            discount_applied: menu.discount_applied
          };
        });

        console.log("see:", salesOrder);
      }
      this.isLoading = false;
    },
    /**
     * Submit the payment based on the selected payment method and amount.
     * This function will first validate if the selected payment method and amount are valid.
     * If valid, it will then call the closeOrder function to close the order.
     * If there is an error, it will display an error message to the user.
     */
    async submitPayment() {
      this.$confirm.require({
        group: 'headless',
        header: 'Apakah anda yakin ingin mengonfirmasi pembayaran ini?',
        message: 'Silahkan konfirmasi sebelum diproses.',
        acceptLabel: 'Ya',
        rejectLabel: 'Tidak',
        accept: async () => {
          try {
            if (!this.selectedPaymentMethodId) {
              this.errorMessage = "Silakan pilih metode pembayaran";
              this.showErrorDialog = true;
              return;
            }

            if (!this.isCashPayment) {
              this.payment = Math.ceil(this.grandTotalFrontEnd);
            }

            if (!this.payment || this.payment <= 0) {
              this.errorMessage = "Silakan masukkan jumlah pembayaran";
              this.showErrorDialog = true;
              return;
            }

            const selectedMethod = this.allPaymentMethods.find(
              (method) => method.id === this.selectedPaymentMethodId
            );

            console.log("Metode Pembayaran:", selectedMethod?.name);
            console.log("Jumlah Bayar:", this.payment);
            console.log("Catatan:", this.notes);

            // Frontend validation
            if (!this.selectedPaymentMethodId) {
              this.errorMessage = "Silakan pilih metode pembayaran terlebih dahulu";
              this.showErrorDialog = true;
              return;
            }
            
            if (!this.isCashPayment) {
              this.payment = Math.ceil(this.grandTotalFrontEnd);
            }

            if (!this.payment || this.payment <= 0) {
              this.errorMessage = "Silakan masukkan jumlah pembayaran";
              this.showErrorDialog = true;
              return;
            }

            if (
              this.payment <
              parseFloat(this.grandTotalFrontEnd)
            ) {
              console.log(
                this.payment,
                parseFloat(this.grandTotalFrontEnd)
              );
              this.errorMessage = "Jumlah pembayaran kurang dari total tagihan";
              this.showErrorDialog = true;
              return;
            }

            await this.closeOrder();
            this.$toast.add({ severity: 'success', summary: 'Success', detail: 'Berhasil mengonfirmasi pembayaran.', life: 3000 });
          }
          catch (error) {
            console.error('Gagal mengonfirmasi pembayaran.', error);
            this.$toast.add({ severity: 'error', summary: 'Failed', detail: error.message || "Terjadi kesalahan saat memproses pembayaran", life: 3000 });
          }
        },
        reject: () => {
          this.$toast.add({ severity: 'error', summary: 'Rejected', detail: 'Batal mengonfirmasi pembayaran.', life: 3000 });
        }
      });
    },
    selectPaymentMethod(method) {
      this.selectedPaymentMethodId = method.id;
      // Set payment amount based on payment method
      if (method.name.toLowerCase() === "cash") {
        this.payment = 0;
        this.isPreviewQrcode = true;
      } else if (method.name.toLowerCase() === "hutang") {
        this.isPreviewQrcode = true;
      } else {
        // this.payment = this.transaction?.grand_total;
        this.payment =
          this.grandTotalFrontEnd;
        this.isPreviewQrcode = false;
      }
    },
    async closeOrder() {
      try {
        const routeQuery = this.$route.query;

        let orderId = Number(routeQuery.orderId);

        console.log("before submit:", {
          id: orderId,
          transaction: this.transaction,
          transaction_id: orderId,
          marketing_id: this.transaction.marketing_id,
          payment_method_id: this.selectedPaymentMethodId,
          cash_register_id: this.transaction.cash_register_id,
          payment_amount: this.payment,
          global_discount_id: this.selectedGlobalDiscountId,
          note: this.notes,
          delivery_fee: this.deliveryFee,
        });

        const response = await this.closeSalesOrder({
          id: orderId,
          transaction_id: orderId,
          marketing_id: this.isHutangPayment.isMarketing ? this.marketingId : this.transaction.marketing_id,
          payment_method_id: this.selectedPaymentMethodId,
          cash_register_id: this.transaction.cash_register_id,
          payment_amount: parseFloat(this.payment),
          global_discount_id: this.selectedGlobalDiscountId,
          note: this.notes,
          customer_name: this.isHutangPayment.isName ? this.customerName : this.transaction?.customer_name,
          customer_phone: this.isHutangPayment.isHp ? this.customerPhone : this.transaction?.customer_phone,
          is_credit: this.isHutangPayment.isHutang,
          outlet_id: this.transaction.outlet_id,
          delivery_fee: this.deliveryFee,
        });

        console.log(response);

        // Reset form
        this.selectedPaymentMethodId = null;
        this.payment = 0;
        this.notes = "";
        this.showPaymentForm = false;

        
        // Reload data
        await this.checkAndRefreshOrder();
        this.printDocumentBluetooth("receipt-print");
      } catch (error) {
        console.error('Failed to save item:', error);

        if (error.status === 400 && error.response.data.errors) {
          this.validationErrors(error.response.data.errors);
          this.$toast.add({ severity: 'error', summary: 'Failed', detail: error.response.data.message, life: 5000 });
        }
        else {
          this.$toast.add({ severity: 'error', summary: 'Rejected', detail: error.response.data.message, life: 5000 });
        }
      }
    },
    async printDocumentBluetooth(type) {
      try {

        if (!this.isConnected) {
          this.$toast.add({
            severity: 'warn',
            summary: 'Printer Tidak Terhubung',
            detail: 'Silakan sambungkan printer Bluetooth terlebih dahulu',
            life: 3000
          });
          return;
        }

        this.startPrinterJob();

        // Get the formatted receipt content
        const formattedContent = this.formatElementForPrinting(type);
        
        // Convert content to Uint8Array (ESC/POS commands)
        const encoder = new TextEncoder('utf-8');
        
        // Initialize printer commands
        const initCmds = new Uint8Array([
          0x1B, 0x40,        // Initialize printer
          0x1B, 0x61, 0x01   // Center align
        ]);

        // Send initialization commands first
        await this.sendInChunks(initCmds, 100);
        await new Promise(r => setTimeout(r, 100));

        // Send receipt content in chunks
        const contentBytes = encoder.encode(formattedContent);
        await this.sendInChunks(contentBytes, 100);
        await new Promise(r => setTimeout(r, 100));

        // Add QRIS section if available
        this.qrCode = selectedQris(new Date());
        if (this.qrCode?.code_qris && (this.transaction?.status !== "paid" || type !== "receipt-print")) {
          // Add QRIS header
          const qrHeader = '\n=== QRIS PAYMENT ===\n';
          await this.sendInChunks(encoder.encode(qrHeader), 100);
          await new Promise(r => setTimeout(r, 100));

          // Add QRIS name
          const qrName = `Bank: ${this.qrCode.name}\n`;
          await this.sendInChunks(encoder.encode(qrName), 100);
          await new Promise(r => setTimeout(r, 100));

          // Add QR code
          const qrText = this.qrCode.code_qris;
          const qrCode = print.buildQrCodeCommand(qrText);
          
          // Send QR code with proper initialization
          const qrInit = new Uint8Array([
            0x1D, 0x28, 0x6B, 0x03, 0x00, 0x31, 0x43, 0x08,  // Set QR code size
            0x1D, 0x28, 0x6B, 0x03, 0x00, 0x31, 0x45, 0x31   // Set QR code error correction
          ]);
          
          await this.sendInChunks(qrInit, 100);
          await this.sendInChunks(qrCode, 100);
          await new Promise(r => setTimeout(r, 300)); // Give more time for QR code processing
          
          // Add footer after QR code
          const qrFooter = '\nScan QRIS untuk pembayaran\n';
          await this.sendInChunks(encoder.encode(qrFooter), 100);
          await new Promise(r => setTimeout(r, 100));
        }
        
        // Add line feeds and cut paper
        const endCmds = new Uint8Array([
          0x0A, 0x0A, 0x0A, 0x0A,  // Line feeds
          0x1D, 0x56, 0x41, 0x10   // Cut paper
        ]);
        
        await this.sendInChunks(endCmds, 100);
        
        this.$toast.add({
          severity: 'success',
          summary: 'Cetak Berhasil',
          detail: 'Struk berhasil dikirim ke printer',
          life: 3000
        });
        
      } catch (error) {
        console.error('Print error:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Gagal Mencetak',
          detail: error.message || 'Terjadi kesalahan saat mencetak',
          life: 5000
        });
      } finally {
        this.endPrinterJob();
      }
    },
    
    async sendInChunks(data, chunkSize = 500) {
      if (!this.isConnected) {
        throw new Error('Printer tidak terhubung');
      }

      const chunks = [];
      for (let i = 0; i < data.length; i += chunkSize) {
        chunks.push(data.slice(i, i + chunkSize));
      }

      for (const chunk of chunks) {
        await this.sendToPrinter(chunk);
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    },
    
    async printToBluetooth(receiptType = 'bill-print') {
      if (!this.isConnected) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Printer Tidak Terhubung',
          detail: 'Silakan sambungkan printer Bluetooth terlebih dahulu',
          life: 3000
        });
        return;
      }
      
      try {
        // Get the content to print
        const printSection = document.getElementById(receiptType);
        if (!printSection) {
          throw new Error(`Print section ${receiptType} not found`);
        }
        
        // Print to Bluetooth
        await this.printToBluetooth(receiptType);
        
        this.$toast.add({
          severity: 'success',
          summary: 'Sent to Printer',
          detail: 'Print job sent to Bluetooth printer',
          life: 3000
        });
        
      } catch (error) {
        console.error('Print error:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Print Error',
          detail: error.message,
          life: 5000
        });
      }
    },
    formatElementForPrinting(divId) {
      let printText = "";
      let totalDiscount = 0;
      let totalPrice = 0;
      const rows = [];

      // Add company header with center alignment
      rows.push("\x1b\x61\x01"); // Center alignment
      rows.push(process.env.VUE_APP_STORE_NAME);
      rows.push(process.env.VUE_APP_STORE_ADDRESS);
      rows.push(process.env.VUE_APP_STORE_PHONE);
      rows.push(print.breakLine(40, "="));

      // Add transaction details
      rows.push(
        print.twoColumn(`No. Invoice:`, `${this.transaction?.code || "-"}`, 17, 6, 17)
      );
      rows.push(
        print.twoColumn(
          `Tanggal:`,
          `${this.formatDate(this.transaction?.createdAt)}`,
          17, 6, 17
        )
      );
      rows.push(
        print.twoColumn(
          `Nama Tamu:`,
          `${this.transaction?.customer_name || "-"}`,
          17, 6, 17
        )
      );
      rows.push(
        print.twoColumn(
          `Kasir:`,
          `${this.transaction?.cashier_name || "-"}`,
          17, 6, 17
        )
      );
      rows.push(print.breakLine(40, "="));

      // Add order items
      this.order_items.forEach((item) => {
        rows.push(print.oneLine(`${item.name}`, 40));
        rows.push(
          print.twoColumn(
            `  ${item.quantity} x ${item.status == "canceled" ? "-" : ""
            }Rp ${Number(item.price).toLocaleString()}`,
            `${item.status == "canceled" ? "-" : ""
            }Rp${Number(item.subtotal).toLocaleString()}`,
            17, 6, 17
          )
        );
        let subTotal = Number(item.price) * Number(item.quantity);
        totalPrice += (item.status == "canceled" ? -1 : 1) * subTotal;
        if (item.discount_price > 0 && item.status != "canceled") {
          rows.push(
            print.twoColumn(
              `  Discount:`,
              `-Rp${Number(item.discount_price).toLocaleString()}`,
              17, 6, 17
            )
          );
          totalDiscount +=
            (item.status == "canceled" ? -1 : 1) *
            (item.discount_price * item.quantity);
        }
        if (item.discount_applied && item.globalDiscountAmount > 0 && item.status != "canceled") {
          rows.push(
            print.twoColumn(
              `  ${this.currentGlobalDiscount?.name || ""}`,
              `-Rp${Number(item.globalDiscountAmount * item.quantity).toLocaleString()}`,
              17, 6, 17
            )
          );
        }
      });

      rows.push(print.breakLine(40, "-"));

      // Add payment details with proper spacing
      rows.push(
        print.twoColumn(
          `Sub Total:`,
          `Rp${Number(totalPrice).toLocaleString()}`,
          17, 6, 17
        )
      );

      if (totalDiscount > 0) {
        rows.push(
          print.twoColumn(
            `Diskon:`,
            `-Rp${Number(totalDiscount).toLocaleString()}`,
            17, 6, 17
          )
        );
      }
      if (this.globalDiscountTotalFrontEnd > 0) {
        rows.push(
          print.twoColumn(
            `${this.currentGlobalDiscount?.name || ""}:`,
            `-Rp${Number(this.globalDiscountTotalFrontEnd).toLocaleString()}`,
            17, 6, 17
          )
        );
      }
      rows.push(
        print.twoColumn(
          `Service Charge ${Number(this.allSettings[0]?.service_charge)}%:`,
          `Rp${Number(this.serviceChargeTotalFrontEnd).toLocaleString()}`,
          17, 6, 17
        )
      );
      rows.push(
        print.twoColumn(
          `Tax ${Number(this.allSettings[0]?.tax)}%:`,
          `Rp${Number(this.serviceTaxTotalFrontEnd).toLocaleString()}`,
          17, 6, 17
        )
      );

      // Add delivery fee if bill print or receipt print
      if ((divId === "bill-print" && this.deliveryFee > 0) || (divId === "receipt-print" && this.transaction.delivery_fee > 0)) {
        rows.push(
          print.twoColumn(
            `Delivery Fee:`,
            `Rp${Number(divId === "bill-print" ? this.deliveryFee : this.transaction.delivery_fee).toLocaleString()}`,
            17, 6, 17
          )
        );
      }
      rows.push(
        print.twoColumn(
          `Total:`,
          `Rp${Number(this.grandTotalFrontEnd).toLocaleString()}`,
          17, 6, 17
        )
      );

      // Add payment info if paid
      if (this.transaction?.status === "paid" || divId === "receipt-print") {
        rows.push(print.breakLine(40, "-"));
        rows.push(
          print.twoColumn(
            `Payment Method:`,
            `${this.transaction?.payment_method?.name || "-"}`,
            17, 6, 17
          )
        );
        if (this.transaction?.payment_amount) {
          rows.push(
            print.twoColumn(
              `Paid Amount:`,
              `Rp${Number(this.transaction?.payment_amount).toLocaleString()}`,
              17, 6, 17
            )
          );
        }
        if (this.transaction?.payment_change) {
          rows.push(
            print.twoColumn(
              `Change:`,
              `Rp${Number(this.transaction?.payment_change).toLocaleString()}`,
              17, 6, 17
            )
          );
        }
      }

      // Add footer with center alignment
      rows.push("\x1b\x61\x01"); // Center alignment
      rows.push(print.breakLine(40, "="));

      if (divId === "receipt-print") {
        rows.push("Terima Kasih Atas Kunjungan Anda");
        rows.push(print.centerTextByCharacter("Simpan Struk ini sebagai bukti pembayaran", 40));
      } else {
        rows.push("Terima Kasih Atas Pembelian Anda!");
      }

      rows.push(print.breakLine(40, "="));
      rows.push(`Jam Cetak: \t ${new Date().toLocaleString()}`);
      rows.push("\n\n\n"); // Extra lines for paper cut

      // Join all rows with newlines
      printText = rows.join("\n");

      return printText;
    },
    async print(divId) {
      try {
        const element = document.getElementById(divId);
        if (!element) {
          throw new Error("Print element not found");
        }

        // Show the element temporarily to capture it
        element.style.display = "block";
        
        // Force font loading and layout calculation before printing
        await new Promise(resolve => {
          // Wait a short time to ensure fonts are loaded and CSS is applied
          setTimeout(() => {
            // Force a reflow
            element.getBoundingClientRect();
            resolve();
          }, 200);
        });

        // Convert the element to text format suitable for ESC/POS
        const printText = await this.formatElementForPrinting(divId);
        console.log("printText:", printText);

        // Hide the element again
        element.style.display = "none";

        // Send to backend printer endpoint
        const print_url =
          process.env.VUE_APP_ROOT_API + "/printer/printing/custom-print";

        const response = await axios.post(print_url, {
          text: printText,
          printer_ip: process.env.VUE_APP_CASIER_IP,
        });
        console.log(response);

        // Optional: Show success message
        console.log("Print job sent successfully");
      } catch (error) {
        console.error("Print error:", error);
        this.errorMessage = "Failed to print: " + error.message;
        this.showErrorDialog = true;
      }
    },
    requestPrint(divId) {
      this.printDivId = divId;
      this.showPrintPreview = true; // Show preview instead of confirmation dialog
    },
    proceedWithPrint() {
      this.showPrintPreview = false;
      this.showPrintConfirmDialog = true;
    },
    async confirmPrint() {
      try {
        await this.print(this.printDivId);
        this.showPrintConfirmDialog = false;
      } catch (error) {
        console.error("Print confirmation error:", error);
        // Error will be handled by print() method
      }
    },
    submitPin(data) {
      const settings = this.allSettings.length > 0 ? this.allSettings[0] : {};
      if (data.pin && data.pin == settings.pin_code) {
        this.selectedGlobalDiscountId = data.selected_id;
      } else {
        this.errorMessage = "PIN yang Anda masukkan salah. Silakan coba lagi.";
        this.showErrorDialog = true;
        this.idGlobalDiscount = null;
        this.selectedGlobalDiscountId = null;
      }
    },
    showModalPIN(product = null) {
      if (['paid', 'canceled'].includes(this.transaction?.status)) {
        this.closePinModal(true);
      } else {
        this.modalShowPin = true;
        this.cancelIdProduct = product ? product.item_id : null;
        this.isBlur = true;
        this.idGlobalDiscount = this.selectedGlobalDiscountId;
      }
    },
    closePinModal(isClose = true) {
      setTimeout(() => {
        this.modalShowPin = false;
        this.isBlur = false;
        this.idGlobalDiscount = null;

        if (isClose) {
          this.selectedGlobalDiscountId = null;
        }
      }, 200);
    },
    validationErrors(errorMessage) {
      for (let index = 0; index < this.fieldCloseOrders.length; index++) {
        this.validationCloseOrders[this.fieldCloseOrders[index]].valid = true;
        this.validationCloseOrders[this.fieldCloseOrders[index]].message = '';
      }

      errorMessage.forEach((error) => {
        if (this.validationCloseOrders[error.field]) {
          this.validationCloseOrders[error.field].valid = false;
          this.validationCloseOrders[error.field].message = error.message;
        }
      });
    },
    printDocument(type) {
      const el = document.getElementById(type);
      const wasHidden = el.classList.contains('hidden');
      if (wasHidden) el.classList.remove('hidden');
      console.log(el);

      // Tunggu DOM update jika pakai Vue reactivity
      setTimeout(() => {
        const printContents = el.innerHTML;
        if (!printContents || printContents.trim() === '') {
          alert('Data print kosong! Pastikan komponen tidak hidden.');
          if (wasHidden) el.classList.add('hidden');
          return;
        }
        const printWindow = window.open('', '', 'height=600,width=800');
        let styles = '';
        document.querySelectorAll('link[rel="stylesheet"], style').forEach(node => {
          styles += node.outerHTML;
        });

        printWindow.document.write(`<html><head><title>Print Bill</title>${styles}<style>@page { size: 48mm 210mm; margin: 0; }body { margin: 0; padding: 0; }</style></head><body>${printContents}</body></html>`);
        printWindow.document.close();

        function pollForReady(win, cb) {
          const interval = setInterval(() => {
            if (win.document.readyState === 'complete') {
              clearInterval(interval);
              cb(win);
            }
          }, 100);
        }
        pollForReady(printWindow, waitForImagesAndPrint);

        function waitForImagesAndPrint(win) {
          const images = win.document.images;
          if (images.length === 0) {
            win.focus();
            // Add delay before printing
            setTimeout(() => {
              win.print();
              win.close();
              if (wasHidden) el.classList.add('hidden');
            }, 500); // 1 second delay
            return;
          }
          let loaded = 0;
          for (let img of images) {
            if (img.complete) {
              loaded++;
            } else {
              img.onload = img.onerror = function () {
                loaded++;
                if (loaded === images.length) {
                  win.focus();
                  // Add delay before printing
                  setTimeout(() => {
                    win.print();
                    win.close();
                    if (wasHidden) el.classList.add('hidden');
                  }, 500); // 1 second delay
                }
              };
            }
          }
          if (loaded === images.length) {
            win.focus();
            // Add delay before printing
            setTimeout(() => {
              win.print();
              win.close();
              if (wasHidden) el.classList.add('hidden');
            }, 500); // 1 second delay
          }
        }
      }, 200); // beri waktu reflow DOM
    },
    closeQrCodeModal() {
      this.modalShowQrCode = false;
    },
    showModalQrCode() {
      this.modalShowQrCode = true;
      this.qrCode = selectedQris(new Date());
    },
    submitQrCode(isPreview) {
      this.modalShowQrCode = false;
      this.qrCode = null;
      this.isPreviewQrcode = isPreview;
    },
  },
};
</script>

<style scoped>
.bg-color-1 {
  background-color: #4a90e2;
}

.bg-color-2 {
  background-color: #357ab7;
}

.print-preview {
  background: white;
  padding: 1rem;
  border-radius: 0.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  min-height: 50vh;
}

/* Style adjustments for dark mode */
:deep(.dark) .print-preview {
  background: var(--dark-800);
  color: var(--dark-100);
}

/* Print-specific styles */
@media print {

  .print-section,
  .print-section * {
    display: block !important;
  }
}

.hidden {
  display: none;
}
</style>
